<template lang="pug">
.entity-tab.py-0.fluid
  v-container
    v-row.px-0.d-flex
      v-col.pt-1
        h2.px-4.primary--text {{ $tc('OrderLine._name', 2) }}
      v-col.pt-1.px-0(cols="6")
        g-btn.mr-3.white--text.float-right(
          v-if="!isASection()",
          color="primary",
          @click="instance.push({ __id: $uid(), entityState: 4, _Entity: entity.name, orderLineContacts: [] })"
        ) {{ $t('Common.addEntity', { entity: $tc('OrderLine._name', 1) }) }}

    v-row.px-0
      v-col
        v-form
          v-divider(color="light.primary")
          v-tabs#order-lines-tabs(
            v-model="tab",
            center-active,
            show-arrows,
            light,
            next-icon="mdi-arrow-right-circle-outline",
            prev-icon="mdi-arrow-left-circle-outline"
          )
            template(v-for="(item, i) in displayableItems")
              v-tab(v-if="isNotDeleted(item)",
                :key="i",
                :path="`${path}[${i}]`"
              )
                span {{ getTabLabel(item) }}
                div(:class="{ 'tab-blink-animation': isDuplicated(item) }")
          v-tabs-items(v-model="tab")
            template(v-for="(item, i) in displayableItems")
              v-tab-item(
                v-if="isNotDeleted(item)",
                :key="i"
              )
                v-divider(color="light.primary")
                //- General Information
                v-row.mt-5
                  v-col(cols="11")
                    h4.mt-1.primaryHover--text.border.primary-header.font-weight-bold {{ $t('OrderLine.generalInfo') }}
                  v-col.d-flex.justify-end(cols="1")
                    v-icon.mr-2(@click="duplicate(item)") mdi-content-duplicate
                    v-icon(@click="setAsDeleted($event, i, item)") mdi-close
                v-row
                  v-col
                    v-text-field(
                      :value="item.externalId",
                      :label="$t('OrderLine.externalId')",
                      disabled,
                      placeholder=" "
                    )
                  v-col
                    v-text-field(
                      v-model="item.urgencyLevel",
                      :label="$t('OrderLine.urgencyLevel')",
                      placeholder=" "
                    )
                  v-col
                    v-select(
                      :label="$t('OrderLine.status')",
                      :items="orderlineStatuses",
                      item-text="name",
                      item-value="id",
                      :value="item.statusId",
                      return-object,
                      @input="changeStatus($event, i, item)"
                    )
                  v-col
                    app-datetime-picker(
                      :value="item.createdAt",
                      :label="$t('OrderLine.createdAt')",
                      disabled,
                      placeholder=" "
                    )
                  v-col
                    app-datetime-picker(
                      :value="item.modifiedAt",
                      :label="$t('OrderLine.modifiedAt')",
                      disabled,
                      placeholder=" "
                    )

                v-divider(color="light.primary")
                //- Item details
                v-row.mt-5
                  v-col(cols="12")
                    h4.mt-1.primaryHover--text.border.primary-header.font-weight-bold {{ $t('OrderLine.itemDetails') }}
                v-row
                  //- Product
                  v-col.mt-1(cols="6")
                    v-row
                      v-col(cols="3")
                        v-text-field(
                          v-model="item.requestedQuantity",
                          :label="$t('OrderLine.requestedQuantity')",
                          type="number",
                          placeholder=" "
                        )
                      v-col(cols="3")
                        v-text-field(
                          v-model="item.availableQuantity",
                          :label="$t('OrderLine.availableQuantity')",
                          type="number",
                          placeholder=" ",
                          disabled
                        )
                      v-col(cols="3")
                        v-select(
                          :label="$t('OrderLine.unitType')",
                          :items="handlingUnitsList",
                          item-text="name",
                          item-value="name",
                          v-model="item.unitType"
                        )
                        v-row
                      v-col(cols="4")
                        v-text-field(
                          v-model="item.unitPrice",
                          :label="$t('OrderLine.unitPrice')",
                          type="number",
                          step="0.01",
                          placeholder=" "
                        )
                      v-col(cols="4")
                        v-autocomplete(
                          :label="$t('OrderLine.currency')",
                          :items="currencies",
                          :item-text="currencyDisplayText",
                          item-value="code",
                          :loading="currencyLoading",
                          v-model="item.currency",
                          :error-messages="errors['OrderLine.currency']",
                        )
                    v-row
                      app-entity-relation(
                        :relation="productRelation",
                        :entity="$findEntity('Product')",
                        :parent="$findEntity('OrderLine')",
                        :extraLoad="['product']",
                        :parentInstance="item",
                        :parentPath="`orderLines[${i}]`",
                        :path="`orderLines[${i}].product`",
                        vertical,
                        @close="$emit('close')"
                      )

                  //- Pickup & Delivery
                  v-col(cols="6")
                    v-row
                      v-col(cols="12")
                        app-daterange-picker(
                          type="Pickup",
                          :datetimeFrom.sync="item.datePickupFrom",
                          :datetimeTo.sync="item.datePickupTo",
                          :specificTime.sync="item.specificPickupTime",
                          :visibleHeader="false",
                          :errors="errors",
                          parentName="orderLines",
                          :parentIndex="i"
                        )
                    v-row
                      v-col(cols="12")
                        app-daterange-picker(
                          type="Delivery",
                          :datetimeFrom.sync="item.dateDeliveryFrom",
                          :datetimeTo.sync="item.dateDeliveryTo",
                          :specificTime.sync="item.specificDeliveryTime",
                          :visibleHeader="false",
                          :errors="errors",
                          parentName="orderLines",
                          :parentIndex="i"
                        )

                v-row(
                  v-if="extrafieldGroups",
                  v-for="group in extrafieldGroups"
                )
                  v-col(cols="12")
                    v-divider(color="light.primary")
                    h4.mt-8.primaryHover--text.border.primary-header.font-weight-bold {{ $camelToString($displayName(group.label)) || $camelToString($displayName(group.name)) }}
                  v-col.py-0(cols="12")
                    h4.font-weight-light.mt-4 Description: {{ group.description }}
                  v-col(
                    cols="3",
                    v-for="field in $sortByUISequence(group.fields)"
                  )
                    slot(
                      :name="field.name",
                      :label="field.name",
                      :type="field.type"
                    )
                    foo-field(
                      :entity="group",
                      :field="field",
                      :label="field.label || field.name",
                      :type="$isFieldList(field, group) ? 'list' : field.type",
                      :value="getValue(field, i)",
                      @change="input($event === null ? false : $event, field, i)",
                      @input="input($event, field, i)",
                      :inputValue="getValue(field, i)",
                      :checked="getValue(field, i) === true",
                      :disabled="field.disabled || disabled",
                      :items="$getFieldOptions(field, group)",
                      :errorMessages="errorInstance[errorHash + '[' + i + '].' + $displayName(group.name) + '.' + field.name]"

                    )

                v-divider(color="light.primary")
                //- Collection Point

                v-row.mt-5
                  v-col.py-2(cols="12")
                    h4.mt-1.primaryHover--text.border.primary-header.font-weight-bold {{ $t('OrderLine.collectionPoint') }}
                v-row
                  v-col.py-1
                    app-party-contacts(
                      :value="getOrderlineContactsFrom(i)",
                      @input="setOrderlineContacts($event, i, 0)",
                      v-bind="{ slotProps }",
                      type="From",
                      vertical,
                      :entity="$findEntity('Party')",
                      :showTitle="false",
                      :parentPath="`orderLines[${i}]`",
                      :path="`orderLines[${i}].collectionPoint`",
                      :parentInstance="item",
                      :parent="$findEntity('OrderLine')",
                      :relation="collectionPointRelation",
                      entityAlias="OrderLineContact"
                    )

                v-divider(color="light.primary")
                //- Delivery Point
                v-row.mt-5
                  v-col.py-2(cols="12")
                    h4.mt-1.primaryHover--text.border.primary-header.font-weight-bold {{ $t('OrderLine.deliveryPoint') }}
                v-row
                  v-col.py-1
                    app-party-contacts(
                      :value="getOrderlineContactsTo(i)",
                      @input="setOrderlineContacts($event, i, 1)",
                      v-bind="{ ...slotProps, showInfo: true }",
                      type="To",
                      vertical,
                      :entity="$findEntity('Party')",
                      :parentPath="`orderLines[${i}]`",
                      :path="`orderLines[${i}].deliveryPoint`",
                      :parentInstance="item",
                      :parent="$findEntity('OrderLine')",
                      :relation="deliveryPointRelation",
                      entityAlias="OrderLineContact"
                    )
</template>

<style scoped>
#order-lines-tabs .v-item-group .v-slide-group__prev {
  position: absolute;
  right: 30px;
  top: 12px;
}

#order-lines-tabs .v-tabs-bar .v-slide-group__next {
  min-width: 80px;
  align-content: flex-end;
}

#order-lines-tabs .v-tabs-bar .v-slide-group__next i {
  display: flex;
  align-content: flex-start;
  right: -15px;
}

#order-lines-tabs
  .v-tabs-bar
  .v-slide-group__wrapper
  .v-slide-group__content
  .v-tab {
  text-transform: none;
}

#order-lines-tabs
  .v-tabs-bar
  .v-slide-group__wrapper
  .v-slide-group__content
  .v-tab--active {
  font-weight: 800;
}
</style>

<script>
import EntityMixin from "@/mixins";
import EntitiesMixin from "@/mixins/entities.mixin";

export default {
  mixins: [EntityMixin,EntitiesMixin],
  props: [
    "entity",
    "chips",
    "invert",
    "path",
    "parentPath",
    "parent",
    "extraData",
    "parentInstance",
    "selectorOnly",
    "showInfo",
    "isSection",
    "isActive",
    "orderlineStatuses",
    "handlingUnitsList",
    "errors",
  ],
  data: function () {
    return {
      tab: null,
      tabDisplay: 1,
      orderlineContactsFrom: [],
      orderlineContactsTo: [],
      slotProps: null,
      currencies: [],
      currencyLoading: true
    };
  },
  computed: {
    displayableItems() {
      return this.instance.length > 0
        ? this.instance.filter((i) => i.entityState != 2)
        : [];
    },
    productRelation() {
      let foundRelations = this.relations.filter((r) => r.name == "product");
      return foundRelations.length === 1 ? foundRelations[0] : {};
    },
    collectionPointRelation() {
      let foundRelations = this.relations.filter(
        (r) => r.name == "collectionPoint"
      );
      return foundRelations.length === 1 ? foundRelations[0] : {};
    },
    deliveryPointRelation() {
      let foundRelations = this.relations.filter(
        (r) => r.name == "deliveryPoint"
      );
      return foundRelations.length === 1 ? foundRelations[0] : {};
    },
    extrafieldGroups() {
      return this.relations.filter((r) => r.extra);
    },
    errorInstance() {
      return this.modalInstance && !_.isEmpty(this.modalErrors)
        ? this.modalErrors
        : this.errors;
    },
    errorHash() {
      if (this.isExtraFieldGroup) return this.extraLabelHash;
      return this.path ? this.path : this.$lcfirst(this.entity.name);
    },
    isExtraFieldGroup() {
      return Boolean(_.get(this.entity, "extra"));
    },
    extraLabelHash() {
      return this.$displayName(
        _.get(this.relation, "name", _.get(this.entity, "name"))
      );
    },
  },
  methods: {
    isASection() {
      if (this.isSection == null || this.isSection == "false") return false;
      return true;
    },
    duplicate(orderLine) {
      const newOrderLine = { ...orderLine, __duplicate: true, id: 0, externalId: null, entityState: 4, uid: this.$generateUid()};
      newOrderLine.extraData = !newOrderLine.extraData ? [] : newOrderLine.extraData.map(({ extraFieldId, type, value }) => ({ entityState: 4, extraFieldId, new: true, type, value }));
      this.instance.push(newOrderLine);
      const index = this.instance.length - 1;
      
      setTimeout(() => {
        newOrderLine.__duplicate = false;
        this.instance.splice(index, 1, newOrderLine);
      }, 500);
    },
    setAsDeleted(element, i, instance) {
      if (instance && instance.id)
        this.$store.commit("SET_INSTANCE_PATH", {
          path: `${this.path}[${i}].entityState`,
          value: 2,
        });
      else if (!instance && element)
        this.$store.commit("SET_INSTANCE_PATH", {
          path: `${this.path}[${i}].entityState`,
          value: 2,
        });
      else {
        this.instance.splice(i, 1);
      }
    },
    changeStatus(element, i, instance) {
      this.$store.commit('SET_INSTANCE_PATH', { path: this.path + '['+i+'].statusId', value: element.id });
      this.$store.commit('SET_INSTANCE_PATH', { path: this.path + '['+i+'].status', value: element });
    },
    getTabLabel(item) {
      let index = this.displayableItems.indexOf(item);
      return `${this.$t("OrderLine.line")} ${index + 1}`;
    },
    isDuplicated(element) {
      return _.get(element, "__duplicate");
    },
    isNotDeleted(element) {
      return !(_.get(element, "entityState") === 2);
    },
    showExtraFields(item) {
      return item.extraData && !item.isExtraFieldGroup;
    },
    getValue(field, orderlineId) {
      if (!field.extra)
        return this.instance[orderlineId][this.$lcfirst(field.name)];
      return this.getExtraData(
        null,
        field,
        this.instance[orderlineId].extraData || []
      ).value;
    },
    getExtraData($event, field, extraData) {
      return (
        extraData.find(
          (e) => Number(e.extraFieldId) === Number(field.extraFieldId)
        ) || {
          type: field.type,
          value: $event,
          extraFieldId: field.extraFieldId,
        }
      );
    },
    input ($event, field, orderlineId) {
      let fieldCopy = { ...field };
      fieldCopy.name = this.$lcfirst(fieldCopy.name)
      if (!fieldCopy.extra)
        this.$store.commit(this.setInstancePathAction, { path: this.path + '[' + orderlineId + '].' + fieldCopy.name, value: $event })
      else
        this.inputExtra($event, fieldCopy, orderlineId);
    },
    inputExtra ($event, field, orderlineId) {
      var extraData = this.instance[orderlineId].extraData || []
      var data = this.getExtraData($event, field, extraData)
      data.value = $event
      data.entityState = data.id ? 3 : 4
      if (data.uid === undefined && !data.new) {
        data.new = true
        extraData.push(data)
      }
      this.$store.commit(this.setInstancePathAction, { path: this.path + '[' + orderlineId + ']' + '.extraData', value: extraData });
    },
    setEntityState() {
      var entityState = this.instance && this.instance.id ? 3 : 4;
      this.$store.commit(this.setInstancePathAction, {
        path: `${this.path}.entityState`,
        value: entityState,
      });
    },
    setEntityName() {
      this.$store.commit(this.setInstancePathAction, {
        path: `${this.path}._Entity`,
        value: this.entity.name,
      });
    },
    getOrderlineContactsFrom(orderlineIndex) {
      let contacts = _.get(this.instance[orderlineIndex], "orderLineContacts");
      return contacts ? contacts.filter((x) => x.contactType == 0) : [];
    },
    getOrderlineContactsTo(orderlineIndex) {
      let contacts = _.get(this.instance[orderlineIndex], "orderLineContacts");
      return contacts ? contacts.filter((x) => x.contactType == 1) : [];
    },
    setOrderlineContacts(contactsList, orderlineIndex, contactType) {
      //--------------
      //CONTACTS FROM
      //--------------
      if (contactType === 0) {
        //1. Get Contacts To for concatenate
        let _contactsTo =
          this.instance[orderlineIndex].orderLineContacts.filter(
            (x) => x.contactType == 1
          ) || [];
        //2. Concatenate
        this.instance[orderlineIndex].orderLineContacts =
          _contactsTo.concat(contactsList);
      }
      //-------------
      // CONTACTS TO
      //-------------
      else if (contactType === 1) {
        //1. Get Contacts From for concatenate
        let _contactsFrom =
          this.instance[orderlineIndex].orderLineContacts.filter(
            (x) => x.contactType == 0
          ) || [];
        //2. Concatenate
        this.instance[orderlineIndex].orderLineContacts =
          _contactsFrom.concat(contactsList);
      }
    },
    async initializeCurrencies() {
      this.currencyLoading = true;
      this.currencies = await this.loadCurrencies();
      this.currencyLoading = false;
    },
  },
  async created() {
    await this.initializeCurrencies();
  },
};
</script>
